import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  Hunt,
  HuntStatus,
  Pin,
  UserParticipation,
  ParticipantSummary,
  HunterProgress,
  ParticipantRole,
} from '@gps-hunt/shared';

interface HuntState {
  // Current hunt data
  currentHunt: Hunt | null;
  pins: Pin[];
  participants: ParticipantSummary[];
  myParticipation: UserParticipation | null;
  myRole: ParticipantRole | null;

  // Hunter-specific state
  currentPinIndex: number;
  hunterProgress: HunterProgress[];

  // Loading states
  isLoading: boolean;
  error: string | null;

  // Actions
  setCurrentHunt: (hunt: Hunt | null) => void;
  updateHuntStatus: (status: HuntStatus) => void;
  setPins: (pins: Pin[]) => void;
  addPin: (pin: Pin) => void;
  setParticipants: (participants: ParticipantSummary[]) => void;
  addParticipant: (participant: ParticipantSummary) => void;
  removeParticipant: (userId: string) => void;
  setParticipantReady: (userId: string, isReady: boolean) => void;
  updateParticipant: (participant: ParticipantSummary) => void;
  setMyParticipation: (participation: UserParticipation | null) => void;
  setMyRole: (role: ParticipantRole | null) => void;
  setCurrentPinIndex: (index: number) => void;
  setHunterProgress: (progress: HunterProgress[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

const initialState = {
  currentHunt: null,
  pins: [],
  participants: [],
  myParticipation: null,
  myRole: null,
  currentPinIndex: 0,
  hunterProgress: [],
  isLoading: false,
  error: null,
};

export const useHuntStore = create<HuntState>()(
  persist(
    (set) => ({
      ...initialState,

      setCurrentHunt: (hunt) => set({ currentHunt: hunt }),

      updateHuntStatus: (status) =>
        set((state) => ({
          currentHunt: state.currentHunt
            ? { ...state.currentHunt, status }
            : null,
        })),

      setPins: (pins) => set({ pins }),

      addPin: (pin) =>
        set((state) => ({
          pins: [...state.pins, pin],
        })),

      setParticipants: (participants) => set({ participants }),

      addParticipant: (participant) =>
        set((state) => {
          const exists = state.participants.some(p => p.userId === participant.userId);
          if (exists) return state;
          return { participants: [...state.participants, participant] };
        }),

      removeParticipant: (userId) =>
        set((state) => ({
          participants: state.participants.filter(p => p.userId !== userId),
        })),

      setParticipantReady: (userId, isReady) =>
        set((state) => ({
          participants: state.participants.map(p =>
            p.userId === userId ? { ...p, isReady } : p
          ),
        })),

      updateParticipant: (participant) =>
        set((state) => ({
          participants: state.participants.map((p) =>
            p.userId === participant.userId ? participant : p
          ),
        })),

      setMyParticipation: (participation) => set({ myParticipation: participation }),

      setMyRole: (role) => set({ myRole: role }),

      setCurrentPinIndex: (index) => set({ currentPinIndex: index }),

      setHunterProgress: (progress) => set({ hunterProgress: progress }),

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error }),

      reset: () => set(initialState),
    }),
    {
      name: 'hunt-storage',
      partialize: (state) => ({
        currentHunt: state.currentHunt,
        myRole: state.myRole,
      }),
    }
  )
);